/** Item types use BrickLink's names. XML formats (BSX, BrickLink XML) use the one-letter codes. */
export const ITEM_TYPES = [
  'PART',
  'SET',
  'MINIFIG',
  'BOOK',
  'GEAR',
  'CATALOG',
  'INSTRUCTION',
  'ORIGINAL_BOX',
  'UNSORTED_LOT',
] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

const TO_LETTER: Record<ItemType, string> = {
  PART: 'P',
  SET: 'S',
  MINIFIG: 'M',
  BOOK: 'B',
  GEAR: 'G',
  CATALOG: 'C',
  INSTRUCTION: 'I',
  ORIGINAL_BOX: 'O',
  UNSORTED_LOT: 'U',
};

const FROM_LETTER: Record<string, ItemType> = Object.fromEntries(
  Object.entries(TO_LETTER).map(([type, letter]) => [letter, type as ItemType]),
);

const ALIASES: Record<string, ItemType> = {
  PARTS: 'PART',
  SETS: 'SET',
  MINIFIGURE: 'MINIFIG',
  MINIFIGURES: 'MINIFIG',
  MINIFIGS: 'MINIFIG',
  INSTRUCTIONS: 'INSTRUCTION',
  BOX: 'ORIGINAL_BOX',
  ORIGINALBOX: 'ORIGINAL_BOX',
  UNSORTEDLOT: 'UNSORTED_LOT',
};

/** Only parts (and unsorted lots) come in colours. Everything else is kept as colour 0, "(Not Applicable)". */
export const itemUsesColor = (type: ItemType): boolean => type === 'PART' || type === 'UNSORTED_LOT';

export function itemTypeLetter(type: ItemType): string {
  return TO_LETTER[type];
}

export function parseItemType(value: unknown): ItemType | null {
  if (typeof value !== 'string') return null;
  const s = value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!s) return null;
  if ((ITEM_TYPES as readonly string[]).includes(s)) return s as ItemType;
  return FROM_LETTER[s] ?? ALIASES[s] ?? null;
}

export const CONDITIONS = ['N', 'U'] as const;
export type Condition = (typeof CONDITIONS)[number];

export function parseCondition(value: unknown): Condition | null {
  if (typeof value !== 'string') return null;
  const s = value.trim().toUpperCase();
  if (s === 'N' || s === 'NEW') return 'N';
  if (s === 'U' || s === 'USED') return 'U';
  return null;
}

/** Complete, incomplete, sealed. Mostly meaningful for sets and minifigures. */
export const SUB_CONDITIONS = ['C', 'I', 'S'] as const;
export type SubCondition = (typeof SUB_CONDITIONS)[number];

export function parseSubCondition(value: unknown): SubCondition | null {
  if (typeof value !== 'string') return null;
  const s = value.trim().toUpperCase();
  if (s === 'C' || s === 'COMPLETE') return 'C';
  if (s === 'I' || s === 'B' || s === 'INCOMPLETE') return 'I';
  if (s === 'S' || s === 'SEALED') return 'S';
  return null;
}

/** Colour 0 is BrickLink's "(Not Applicable)", used by everything that is not coloured. */
export const NO_COLOR_ID = 0;

const NAMED_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/** BrickLink sends names with HTML codes in them ("&#40;" for a bracket). This turns them back into the characters. */
export function decodeHtmlEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
    if (body[0] === '#') {
      const code = body[1]?.toLowerCase() === 'x' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : whole;
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
  });
}
